import type { ToolCall } from '@ag-ui/client'

/**
 * Mirrors the AI server's persisted message shape (`myhelp-ai-server`
 * `/threads*` endpoints). Deliberately a standalone type rather than an
 * import from the server — the frontend must not depend on server internals.
 */
export type ConversationMessageRole =
  | 'system'
  | 'developer'
  | 'user'
  | 'assistant'
  | 'tool'

export interface ConversationMessage {
  id: string
  role: ConversationMessageRole
  content: string | null
  toolCalls: ToolCall[] | null
  toolCallId: string | null
  name: string | null
  createdAt: string
  seq: number
}

/**
 * A widget (`mcp-apps`) or turn-metadata snapshot the server journalled for a
 * run. Not a message: it travels beside `messages` and is spliced in right
 * after the message named by `afterMessageId`.
 */
export interface ConversationActivity {
  /** The snapshot's `messageId` — unique per emission. */
  id: string
  activityType: string
  content: unknown
  createdAt: string
  afterMessageId: string
}

export interface ConversationSummary {
  id: string
  title: string | null
  createdAt: string
  lastActivityAt: string
  isFork: boolean
}

export interface ConversationPageInfo {
  hasMore: boolean
  nextCursor: string | null
}

export interface ConversationListPage {
  conversations: ConversationSummary[]
  page: ConversationPageInfo
}

export interface ConversationPage {
  conversation: ConversationSummary
  messages: ConversationMessage[]
  /** Absent on servers that predate widget history; treat as empty. */
  activities?: ConversationActivity[]
  page: ConversationPageInfo
}

/** `fetchWholeConversation`'s result: every page, oldest-first. */
export interface StoredConversation {
  messages: ConversationMessage[]
  activities: ConversationActivity[]
}

/**
 * Response shape of both `DELETE /threads/:threadId` and `DELETE /threads` —
 * `deleted` counts threads, not messages (always `1` for the single-thread
 * delete on success).
 */
export interface DeleteResult {
  deleted: number
}

/** `GET /threads/:threadId/share-preview`. */
export interface SharePreview {
  /** True when the chat carries tool calls or results; warn before sharing. */
  containsToolData: boolean
}

/** `POST /threads/:threadId/share`. The raw token is only ever returned here. */
export interface CreatedShare extends SharePreview {
  shareId: string
  token: string
  expiresAt: string
  url?: string
}

/**
 * A message as a link holder sees it: the stored shape minus `seq`, which the
 * server deliberately keeps internal.
 */
export type SharedMessage = Omit<ConversationMessage, 'seq'>

/** One page of `POST /shared/open`. */
export interface SharedConversationPage {
  share: { title: string | null; sharedAt: string; expiresAt: string }
  messages: SharedMessage[]
  /** Absent on servers that predate widget history; treat as empty. */
  activities?: ConversationActivity[]
  page: ConversationPageInfo
  /** Present only when the caller owns the shared chat. */
  ownThreadId?: string
}

/** `fetchWholeShared`'s result: every page, oldest-first. */
export interface SharedConversation {
  share: SharedConversationPage['share']
  messages: SharedMessage[]
  activities: ConversationActivity[]
  ownThreadId?: string
}

/**
 * The envelope every `/threads*` error responds with: `{ error, message }`.
 * Callers must branch on `code` (the `error` field), never on `message` —
 * see the API contract in `plan-recent-chat-integration.md`.
 */
export type ConversationApiErrorCode =
  | 'INVALID_ACCESS_TOKEN'
  | 'INSUFFICIENT_SCOPE'
  | 'CLIENT_NOT_ALLOWED'
  | 'PAGINATION_INVALID'
  | 'THREAD_ID_INVALID'
  | 'THREAD_NOT_FOUND'
  | 'THREAD_FORBIDDEN'
  | 'RUN_ALREADY_ACTIVE'
  | 'INTERNAL_SERVER_ERROR'
  | 'THREAD_EMPTY'
  | 'SHARE_NOT_FOUND'
  | 'SHARE_EXPIRED'
  | 'SHARE_REVOKED'
  | 'SHARE_OWN_THREAD'
  | 'SHARE_REQUEST_INVALID'

export class ConversationApiError extends Error {
  readonly code: ConversationApiErrorCode | string
  readonly status: number

  constructor(
    code: ConversationApiErrorCode | string,
    message: string,
    status: number,
  ) {
    super(message)
    this.name = 'ConversationApiError'
    this.code = code
    this.status = status
  }
}
